import React, { useState } from 'react';

import { useAuth } from '../context/AuthContext';
import { useMap } from '../context/MapContext';
import { useSocket } from '../utils/useSocket';

import { Maintenance } from './admin/Maintenance';
import { Init } from './auth/Init';
import { Login } from './auth/Login';
import { Error } from './common/Error';
import { Offline } from './common/Offline';
import { Splash } from './common/Splash';
import { WaitPosition } from './common/WaitPosition';
import { MapView } from './MapView';

/**
 * AppContent Component - Main application container
 *
 * Manages the overall application state, view routing, and socket connections.
 * Integrates with AuthContext for authentication state management.
 * Handles navigation between different application views (init, login, map, etc.).
 *
 * Views:
 * - init: Initial authentication check
 * - login: User login form
 * - map: Main application interface
 * - offline: Error state when connection is lost
 * - maintenance: Admin maintenance panel
 */
export const AppContent: React.FC = () => {
  const { logout } = useAuth();
  const { waitForPos, setWaitForPos } = useMap();

  // View navigation state
  const [view, setView] = useState<string>('init');
  const [error, setError] = useState<string | null>(null);

  // Incremented each time the admin requests the geofence area editor; Map watches this.
  const [areaEditorRequest, setAreaEditorRequest] = useState(0);

  // Socket connection for real-time updates
  const { socket, handleLogout } = useSocket(setView, logout);

  /**
   * View routing configuration
   * Maps view names to their corresponding components
   */
  const views: Record<string, () => React.ReactElement> = {
    init: () => <Init setView={setView} />,

    login: () => (
      <Login
        setView={setView}
        setError={(err) => {
          setError(typeof err === 'string' ? err : err.message);
        }}
        socket={socket}
      />
    ),

    offline: () => <Offline offline={true} setView={setView} />,

    maintenance: () => (
      <Maintenance
        onClose={() => setView('map')}
        onOpenAreaEditor={() => {
          setAreaEditorRequest((r) => r + 1);
          setView('map');
        }}
      />
    ),

    map: () => (
      <MapView
        socket={socket}
        setView={setView}
        handleLogout={handleLogout}
        openAreaEditorRequest={areaEditorRequest}
      />
    ),
  };

  /**
   * Determine which component to render based on current state
   * Prioritizes error display, then routes to appropriate view
   */
  let body: React.ReactElement;
  if (error) {
    body = <Error text={error} onBack={() => setError(null)} />;
  } else {
    body = views[view] ? (
      views[view]()
    ) : (
      <Error text={`Unknown view: ${view}`} onBack={() => setView('map')} />
    );
  }

  return (
    <div className="App">
      <header className="App-header" style={{ fontSize: '6vmin' }}>
        <div>
          {body}
          <WaitPosition waitForPos={waitForPos} setWaitForPos={setWaitForPos} />
          <Splash />
        </div>
      </header>
    </div>
  );
};

export default AppContent;
